import * as React from "react"

// Shared immutable fallbacks: returning a new [] / {} from a reducer or a selector changes the reference on
// every call and defeats every memoization downstream.
export const EMPTY_ARRAY: any[] = []
export const EMPTY_OBJECT: any = {}

const shallowArrayEqual = (a: readonly unknown[], b: readonly unknown[]) =>
  a.length === b.length && a.every((value, index) => value === b[index])

/**
 * Minimal reselect-like selector: the result is recomputed only when one of the input selectors returns a new
 * reference.
 */
export function createSelector<S, A, R>(
  inputs: [(state: S) => A],
  combiner: (a: A) => R
): (state: S) => R
export function createSelector<S, A, B, R>(
  inputs: [(state: S) => A, (state: S) => B],
  combiner: (a: A, b: B) => R
): (state: S) => R
export function createSelector<S, A, B, C, R>(
  inputs: [(state: S) => A, (state: S) => B, (state: S) => C],
  combiner: (a: A, b: B, c: C) => R
): (state: S) => R
export function createSelector(
  inputs: Array<(state: any) => any>,
  combiner: (...args: any[]) => any
): (state: any) => any {
  let lastArgs: unknown[] | undefined
  let lastResult: unknown
  return (state: any) => {
    const args = inputs.map((input) => input(state))
    if (!lastArgs || !shallowArrayEqual(lastArgs, args)) {
      lastArgs = args
      lastResult = combiner(...args)
    }
    return lastResult
  }
}

const MAX_COMPARED_ARRAY_LENGTH = 50

const isPlainObject = (value: unknown): value is Record<string, unknown> => {
  if (value === null || typeof value !== "object") {
    return false
  }
  const proto = Object.getPrototypeOf(value)
  return proto === Object.prototype || proto === null
}

/**
 * Structural equality for the small configuration objects passed inline as props (styles, dropdown settings,
 * previous-value descriptors...). Functions are compared by reference; long arrays are compared by reference
 * only, so that a large option list never costs more than a pointer comparison. A false negative only causes
 * an extra render, never a stale one.
 */
export const isSimilar = (a: unknown, b: unknown, depth = 5): boolean => {
  if (a === b) {
    return true
  }
  if (depth <= 0 || a === null || b === null || typeof a !== "object" || typeof b !== "object") {
    return false
  }
  if (React.isValidElement(a) || React.isValidElement(b)) {
    if (!React.isValidElement(a) || !React.isValidElement(b)) {
      return false
    }
    return a.type === b.type && a.key === b.key && isSimilar(a.props, b.props, depth - 1)
  }
  if (a instanceof Date || b instanceof Date) {
    return a instanceof Date && b instanceof Date && a.getTime() === b.getTime()
  }
  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) {
      return false
    }
    if (a.length > MAX_COMPARED_ARRAY_LENGTH) {
      return false
    }
    return a.every((value, index) => isSimilar(value, b[index], depth - 1))
  }
  if (!isPlainObject(a) || !isPlainObject(b)) {
    return false
  }
  const keysA = Object.keys(a)
  if (keysA.length !== Object.keys(b).length) {
    return false
  }
  return keysA.every(
    (key) => Object.prototype.hasOwnProperty.call(b, key) && isSimilar(a[key], b[key], depth - 1)
  )
}

/** `React.memo` comparator: each prop is compared with `isSimilar`. */
export const arePropsSimilar = <P extends object>(prev: Readonly<P>, next: Readonly<P>): boolean => {
  const prevKeys = Object.keys(prev)
  if (prevKeys.length !== Object.keys(next).length) {
    return false
  }
  return prevKeys.every((key) => isSimilar((prev as any)[key], (next as any)[key]))
}

/**
 * Returns a function whose identity never changes but which always calls the latest `callback`. Use it for
 * handlers passed to memoized children, so that they do not re-render merely because the parent re-rendered.
 */
export function useEventCallback<A extends unknown[], R>(callback: (...args: A) => R): (...args: A) => R {
  const ref = React.useRef(callback)
  ref.current = callback
  return React.useCallback((...args: A) => ref.current(...args), [])
}

/** `useMemo` whose dependency list may change length (dependencies compared element by element). */
export function useArrayMemo<T>(factory: () => T, deps: readonly unknown[]): T {
  const ref = React.useRef<{ deps: readonly unknown[]; value: T }>()
  if (!ref.current || !shallowArrayEqual(ref.current.deps, deps)) {
    ref.current = { deps, value: factory() }
  }
  return ref.current.value
}

/** Memoizes a function of one object argument (weak cache: entries disappear with the key). */
export function weakMemoize<K extends object, R>(fn: (key: K) => R): (key: K) => R {
  const cache = new WeakMap<K, R>()
  return (key: K) => {
    if (cache.has(key)) {
      return cache.get(key) as R
    }
    const result = fn(key)
    cache.set(key, result)
    return result
  }
}
